import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  Alert,
  Button,
  Form,
  ProgressBar,
  Modal,
  Row,
  Col,
  Spinner
} from 'react-bootstrap';
import WebcamPreview from './WebcamPreview';
import AudioRecorder from './AudioRecorder';
import { apiService } from '../../../services/apiService';

const PracticeQuestionSession = ({ questions, onEndSession }) => {
  const [questionStates, setQuestionStates] = useState(
    questions.map((q) => ({
      question: q,
      userAnswer: '',
      submitted: false,
      feedback: null,
    }))
  );
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [sessionScore, setSessionScore] = useState(0);

  const [isSpeechMode, setIsSpeechMode] = useState(true);
  const [speechFeedback, setSpeechFeedback] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcriptionProcessed, setTranscriptionProcessed] = useState(false);

  const [showWebcam, setShowWebcam] = useState(true);
  const [webcamError, setWebcamError] = useState('');

  const audioRef = useRef(null);

  const currentQuestionState = questionStates[currentQuestionIndex];
  const currentQuestion = currentQuestionState.question;

  useEffect(() => {
    const requestWebcamAccess = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ video: true });
        setWebcamError('');
      } catch (err) {
        setWebcamError(`Webcam access denied: ${err.message}. Please enable camera permissions.`);
        setShowWebcam(false);
      }
    };

    requestWebcamAccess();
  }, []);

  const unansweredQuestions = questionStates.reduce((acc, state, index) => {
    if (!state.submitted) acc.push(index);
    return acc;
  }, []);

  const handleAnswerChange = (e) => {
    const newQuestionStates = [...questionStates];
    newQuestionStates[currentQuestionIndex] = {
      ...newQuestionStates[currentQuestionIndex],
      userAnswer: e.target.value,
    };
    setQuestionStates(newQuestionStates);
  };

  const handleTranscriptionComplete = (transcription) => {
    const newQuestionStates = [...questionStates];
    newQuestionStates[currentQuestionIndex] = {
      ...newQuestionStates[currentQuestionIndex],
      userAnswer: transcription,
    };
    setQuestionStates(newQuestionStates);
    setTranscriptionProcessed(true);
  };

  const handleAudioProcessed = (blob) => {
    setAudioBlob(blob);
    setTranscriptionProcessed(false);
  };

  const handleSubmitAnswer = async () => {
    const currentState = questionStates[currentQuestionIndex];

    if (!currentState.userAnswer.trim()) {
      setError('Please enter an answer before submitting');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('user_answer', currentState.userAnswer);
      formData.append('correct_answer', currentQuestion.answer);
      formData.append('question', currentQuestion.question);

      const data = await apiService.checkAnswer(formData);

      let speechQualityFeedback = null;
      if (audioBlob && isSpeechMode) {
        try {
          const speechFormData = new FormData();
          speechFormData.append('audio', audioBlob);
          speechFormData.append('transcript', currentState.userAnswer);

          speechQualityFeedback = await apiService.assessSpeechQuality(speechFormData);
        } catch (speechErr) {
          console.error('Error assessing speech quality:', speechErr);
        }
      }

      const newQuestionStates = [...questionStates];
      newQuestionStates[currentQuestionIndex] = {
        ...currentState,
        submitted: true,
        feedback: data,
        speechFeedback: speechQualityFeedback,
      };
      setQuestionStates(newQuestionStates);
      setSpeechFeedback(speechQualityFeedback);
    } catch (err) {
      console.error('Error submitting answer:', err);
      setError('Failed to check answer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNextQuestion = () => {
    const nextIndex = unansweredQuestions.find((i) => i > currentQuestionIndex);
    if (nextIndex !== undefined) {
      setCurrentQuestionIndex(nextIndex);
      resetSpeechState();
    } else if (unansweredQuestions.length === 0) {
      calculateSessionScore();
      setShowSummaryModal(true);
    }
  };

  const handlePreviousQuestion = () => {
    const prevIndex = [...unansweredQuestions].reverse().find((i) => i < currentQuestionIndex);
    if (prevIndex !== undefined) {
      setCurrentQuestionIndex(prevIndex);
      resetSpeechState();
    }
  };

  const resetSpeechState = () => {
    setAudioBlob(null);
    setSpeechFeedback(null);
    setTranscriptionProcessed(false);
  };

  const toggleInputMode = () => {
    setIsSpeechMode(!isSpeechMode);
    resetSpeechState();
  };

  const toggleWebcam = () => {
    setShowWebcam(!showWebcam);
    setWebcamError('');
  };

  const handleWebcamError = (errorMsg) => {
    setWebcamError(errorMsg);
    setShowWebcam(false);
  };

  const calculateSessionScore = () => {
    const submittedQuestions = questionStates.filter((state) => state.submitted);
    const totalScore = submittedQuestions.reduce(
      (sum, state) => sum + (state.feedback?.accuracy || 0),
      0
    );
    const avgScore =
      submittedQuestions.length > 0 ? Math.round(totalScore / submittedQuestions.length) : 0;
    setSessionScore(avgScore);
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 80) return 'success';
    if (accuracy >= 60) return 'info';
    if (accuracy >= 40) return 'warning';
    return 'danger';
  };

  return (
    <div className="interview-practice-session" style={{ padding: '20px' }}>
      <Row>
        <Col md={8}>
          <Card className="main-content-card mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5>
                    Question {currentQuestionIndex + 1} of {questions.length}
                  </h5>
                  <div className="text-muted small">Skill: {currentQuestion.skill || currentQuestion.type}</div>
                  <div className="text-muted small">Type: {currentQuestion.type}</div>
                </div>
                <div>
                  <Button
                    variant={isSpeechMode ? 'primary' : 'outline-primary'}
                    className="me-2"
                    onClick={toggleInputMode}
                  >
                    <i className="bi bi-mic"></i> Speech
                  </Button>
                  <Button
                    variant={!isSpeechMode ? 'primary' : 'outline-primary'}
                    className="me-2"
                    onClick={toggleInputMode}
                  >
                    <i className="bi bi-keyboard"></i> Text
                  </Button>
                  <Button
                    variant={showWebcam ? 'primary' : 'outline-primary'}
                    className="me-2"
                    onClick={toggleWebcam}
                  >
                    <i className="bi bi-camera-video"></i> {showWebcam ? 'Hide Camera' : 'Show Camera'}
                  </Button>
                  <Button variant="outline-secondary" onClick={onEndSession}>
                    End Session
                  </Button>
                </div>
              </div>

              {webcamError && <Alert variant="danger">{webcamError}</Alert>}
              {error && <Alert variant="danger">{error}</Alert>}

              <Card className="bg-light mb-3">
                <Card.Body>
                  <Card.Title>Interview Question</Card.Title>
                  <Card.Text>{currentQuestion.question}</Card.Text>
                </Card.Body>
              </Card>

              {!currentQuestionState.submitted && (
                <>
                  {isSpeechMode ? (
                    <div className="mb-4">
                      <h5>Your Spoken Answer</h5>
                      <div className="mb-3">
                        <AudioRecorder 
                          onTranscriptionComplete={handleTranscriptionComplete}
                          onAudioProcessed={handleAudioProcessed}
                          transcribeAudioMock={(audioBlob) => apiService.transcribeAudioMock(audioBlob)}
                        />
                      </div>
                      {transcriptionProcessed && (
                        <Form>
                          <Form.Group className="mb-3">
                            <Form.Label>Your Answer</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={6}
                              value={currentQuestionState.userAnswer}
                              onChange={handleAnswerChange}
                              placeholder="Your transcribed answer will appear here"
                            />
                          </Form.Group>
                        </Form>
                      )}
                      {transcriptionProcessed && (
                        <Button
                          variant="primary"
                          onClick={handleSubmitAnswer}
                          disabled={loading || !currentQuestionState.userAnswer.trim()}
                        >
                          {loading ? (
                            <>
                              <Spinner animation="border" size="sm" /> Checking...
                            </>
                          ) : (
                            'Submit Answer'
                          )}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Form className="mb-4">
                      <Form.Group className="mb-3">
                        <Form.Label>Your Answer</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={6}
                          value={currentQuestionState.userAnswer}
                          onChange={handleAnswerChange}
                          placeholder="Type your answer here..."
                        />
                      </Form.Group>

                      <Button
                        variant="primary"
                        onClick={handleSubmitAnswer}
                        disabled={loading || !currentQuestionState.userAnswer.trim()}
                      >
                        {loading ? (
                          <>
                            <Spinner animation="border" size="sm" /> Checking...
                          </>
                        ) : (
                          'Submit Answer'
                        )}
                      </Button>
                    </Form>
                  )}
                </>
              )}

              {currentQuestionState.submitted && currentQuestionState.feedback && (
                <Card className="mt-4">
                  <Card.Header>
                    <h5 className="mb-0">Content Feedback</h5>
                  </Card.Header>
                  <Card.Body>
                    <p>{currentQuestionState.feedback.feedback}</p>
                  </Card.Body>
                </Card>
              )}

              <div className="d-flex justify-content-between mt-4">
                <Button
                  variant="outline-primary"
                  onClick={handlePreviousQuestion}
                  disabled={currentQuestionIndex === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="primary"
                  onClick={handleNextQuestion}
                >
                  {unansweredQuestions.length > 0 ? 'Next Question' : 'Finish'}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          {showWebcam && (
            <Card className="mb-3">
              <Card.Body style={{ padding: 0, overflow: 'hidden', borderRadius: '8px' }}>
                <WebcamPreview onError={handleWebcamError} />
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      <Modal show={showSummaryModal} onHide={onEndSession} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Interview Practice Session Summary</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-4">
            <h3>Your Overall Score</h3>
            <ProgressBar
              now={sessionScore}
              label={`${sessionScore}%`}
              variant={getAccuracyColor(sessionScore)}
              style={{ height: '30px', width: '50%' }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onEndSession}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PracticeQuestionSession;
